import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Flex, Grid, Heading, HStack, IconButton, Image, Text } from '@chakra-ui/react';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';
import { Link as RouterLink } from 'react-router-dom';
import { pageBg } from '../../theme/themeColor';
import { RiftboundButton } from '../ui/button';
import { CarouselDotIndicator } from '../ui/carousel-dot-indicator';

export type FeatureCarouselDomain = 'Body' | 'Calm' | 'Chaos' | 'Fury' | 'Mind' | 'Order';

export interface FeatureCarouselSlide {
  eyebrow: string;
  title: string;
  body: string;
  mockTitle: string;
  mockDetail: string;
  domain: FeatureCarouselDomain;
  imageSrc?: string;
  imageAlt?: string;
  /** Mobile screenshots use the phone frame; desktop screenshots use a wider window frame. */
  imageVariant?: 'mobile' | 'desktop';
  videoSrc?: string;
  videoPoster?: string;
  ctaLabel?: string;
  ctaTo?: string;
}

export interface FeatureCarouselProps {
  heading: string;
  headingId: string;
  slides: readonly FeatureCarouselSlide[];
  controlsLabel: string;
}

const DOMAIN_GRADIENTS: Record<FeatureCarouselDomain, string> = {
  Body: 'radial-gradient(circle at 82% 22%, rgba(195, 72, 49, 0.34), rgba(195, 72, 49, 0) 34%), linear-gradient(135deg, rgba(195, 72, 49, 0.28), rgba(210, 180, 90, 0.1))',
  Calm: 'radial-gradient(circle at 82% 22%, rgba(72, 178, 187, 0.32), rgba(72, 178, 187, 0) 34%), linear-gradient(135deg, rgba(72, 178, 187, 0.24), rgba(210, 180, 90, 0.08))',
  Chaos: 'radial-gradient(circle at 82% 22%, rgba(154, 65, 201, 0.34), rgba(154, 65, 201, 0) 34%), linear-gradient(135deg, rgba(154, 65, 201, 0.28), rgba(242, 92, 92, 0.1))',
  Fury: 'radial-gradient(circle at 82% 22%, rgba(255, 116, 54, 0.34), rgba(255, 116, 54, 0) 34%), linear-gradient(135deg, rgba(255, 116, 54, 0.28), rgba(210, 180, 90, 0.1))',
  Mind: 'radial-gradient(circle at 82% 22%, rgba(57, 189, 248, 0.34), rgba(57, 189, 248, 0) 34%), linear-gradient(135deg, rgba(57, 189, 248, 0.26), rgba(124, 92, 255, 0.1))',
  Order: 'radial-gradient(circle at 82% 22%, rgba(210, 180, 90, 0.36), rgba(210, 180, 90, 0) 34%), linear-gradient(135deg, rgba(210, 180, 90, 0.32), rgba(140, 118, 55, 0.1))',
};

const CAROUSEL_GUTTER = {
  base: 6,
  md: 8,
  lg: 'max(32px, calc((100vw - 1120px) / 2 + 24px))',
} as const;

/** Shared footprint for screenshot frames and frameless carousel videos. */
const FEATURE_CAROUSEL_DEVICE_MAX_W = { base: '220px', md: '280px' } as const;
const FEATURE_CAROUSEL_DEVICE_ASPECT = '430 / 932';

export function FeatureCarousel({
  heading,
  headingId,
  slides,
  controlsLabel,
}: FeatureCarouselProps) {
  const [activeFeature, setActiveFeature] = useState(0);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const slideRefs = useRef<Array<HTMLDivElement | null>>([]);
  const scrollFrameRef = useRef<number | null>(null);
  const canScrollPrevious = activeFeature > 0;
  const canScrollNext = activeFeature < slides.length - 1;
  const setSlideRef = useCallback((index: number, node: HTMLDivElement | null) => {
    slideRefs.current[index] = node;
  }, []);

  const scrollToFeature = useCallback((index: number) => {
    const scroller = scrollRef.current;
    const nextIndex = Math.min(Math.max(index, 0), slides.length - 1);
    const slide = slideRefs.current[nextIndex];
    if (!scroller || !slide) return;

    const scrollerStyles = window.getComputedStyle(scroller);
    const scrollPaddingStart = Number.parseFloat(scrollerStyles.scrollPaddingInlineStart) || 0;
    const maxScrollLeft = scroller.scrollWidth - scroller.clientWidth;
    const scrollTarget = slide.offsetLeft - scroller.offsetLeft - scrollPaddingStart;

    setActiveFeature(nextIndex);
    scroller.scrollTo({
      left: Math.min(Math.max(scrollTarget, 0), maxScrollLeft),
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }, [slides.length]);

  const previousFeature = useCallback(() => {
    scrollToFeature(activeFeature - 1);
  }, [activeFeature, scrollToFeature]);

  const nextFeature = useCallback(() => {
    scrollToFeature(activeFeature + 1);
  }, [activeFeature, scrollToFeature]);

  useEffect(() => () => {
    if (scrollFrameRef.current !== null) {
      window.cancelAnimationFrame(scrollFrameRef.current);
    }
  }, []);

  const updateActiveFromScroll = useCallback(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;

    const scrollerStyles = window.getComputedStyle(scroller);
    const scrollPaddingStart = Number.parseFloat(scrollerStyles.scrollPaddingInlineStart) || 0;
    const maxScrollLeft = scroller.scrollWidth - scroller.clientWidth;
    const nextActive = slideRefs.current.reduce((closestIndex, slide, index) => {
      if (!slide) return closestIndex;
      const slideScrollTarget = Math.min(
        Math.max(slide.offsetLeft - scroller.offsetLeft - scrollPaddingStart, 0),
        maxScrollLeft,
      );
      const currentDistance = Math.abs(slideScrollTarget - scroller.scrollLeft);
      const closestSlide = slideRefs.current[closestIndex];
      const closestSlideScrollTarget = closestSlide
        ? Math.min(
            Math.max(closestSlide.offsetLeft - scroller.offsetLeft - scrollPaddingStart, 0),
            maxScrollLeft,
          )
        : Number.POSITIVE_INFINITY;
      const closestDistance = closestSlide
        ? Math.abs(closestSlideScrollTarget - scroller.scrollLeft)
        : Number.POSITIVE_INFINITY;
      return currentDistance < closestDistance ? index : closestIndex;
    }, 0);

    setActiveFeature((current) => (current === nextActive ? current : nextActive));
  }, []);

  const onCarouselScroll = useCallback(() => {
    if (scrollFrameRef.current !== null) return;
    scrollFrameRef.current = window.requestAnimationFrame(() => {
      scrollFrameRef.current = null;
      updateActiveFromScroll();
    });
  }, [updateActiveFromScroll]);

  return (
    <Box as="section" w="100%" pt={{ base: 10, md: 16 }} aria-labelledby={headingId}>
      <Flex
        align={{ base: 'flex-start', md: 'flex-end' }}
        justify="space-between"
        gap={{ base: 5, md: 8 }}
        direction={{ base: 'column', md: 'row' }}
        mb={{ base: 8, md: 12 }}
      >
        <Heading
          id={headingId}
          as="h2"
          textAlign="left"
          fontFamily="heading"
          fontSize="clamp(32px, 6.2vw, 48px)"
          fontWeight="bold"
          letterSpacing="-0.045em"
          lineHeight="0.9"
          maxW="22ch"
        >
          {heading}
        </Heading>

        {slides.length > 1 && (
          <HStack gap={2} aria-label={controlsLabel}>
            <IconButton
              aria-label="Previous feature"
              rounded="full"
              variant="outline"
              color="text"
              borderColor="border"
              bg="surface"
              disabled={!canScrollPrevious}
              onClick={previousFeature}
              _hover={{ bg: 'accent', color: 'bg', borderColor: 'accent' }}
            >
              <MdChevronLeft />
            </IconButton>
            <IconButton
              aria-label="Next feature"
              rounded="full"
              variant="outline"
              color="text"
              borderColor="border"
              bg="surface"
              disabled={!canScrollNext}
              onClick={nextFeature}
              _hover={{ bg: 'accent', color: 'bg', borderColor: 'accent' }}
            >
              <MdChevronRight />
            </IconButton>
          </HStack>
        )}
      </Flex>

      <Box
        w="100vw"
        maxW="100vw"
        ml="calc(50% - 50vw)"
        mr="calc(50% - 50vw)"
        overflow="hidden"
      >
      <Box
        ref={scrollRef}
        w="100%"
        maxW="100vw"
        overflowX="auto"
        overflowY="hidden"
        overscrollBehaviorX="contain"
        scrollSnapType="x mandatory"
        scrollPaddingInlineStart={CAROUSEL_GUTTER}
        scrollBehavior="smooth"
        onScroll={onCarouselScroll}
        css={{
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        <Flex
          gap={{ base: 4, md: 6 }}
          align="stretch"
          pl={CAROUSEL_GUTTER}
          pr={CAROUSEL_GUTTER}
          pb={1}
        >
          {slides.map((feature, index) => (
            <FeatureCarouselCard
              key={feature.title}
              feature={feature}
              index={index}
              selected={index === activeFeature}
              setSlideRef={setSlideRef}
            />
          ))}
        </Flex>
      </Box>
      </Box>

      {slides.length > 1 ? (
        <Box mt={{ base: 5, md: 7 }}>
          <CarouselDotIndicator
            count={slides.length}
            progress={activeFeature}
            ariaLabel="Carousel slides"
            labels={slides.map((feature) => `Show ${feature.mockTitle}`)}
            onSelect={scrollToFeature}
          />
        </Box>
      ) : null}
    </Box>
  );
}

function FeatureCarouselCard({
  feature,
  index,
  selected,
  setSlideRef,
}: {
  feature: FeatureCarouselSlide;
  index: number;
  selected: boolean;
  setSlideRef: (index: number, node: HTMLDivElement | null) => void;
}) {
  return (
    <Flex
      ref={(node) => setSlideRef(index, node)}
      flex={{ base: '0 0 calc(100vw - 48px)', md: '0 0 min(72vw, 760px)', lg: '0 0 min(58vw, 760px)' }}
      justify="center"
      scrollSnapAlign="start"
      scrollSnapStop="always"
    >
      <Grid
        w="100%"
        minH={{ base: '460px', md: '540px', lg: '620px' }}
        templateColumns={{ base: '1fr', lg: 'minmax(0, 1.25fr) minmax(220px, 0.48fr)' }}
        gap={{ base: 6, md: 8, lg: 14 }}
        alignItems="stretch"
        textAlign="left"
        p={{ base: 7, md: 12, lg: 16 }}
        position="relative"
        overflow="hidden"
        bg="bgElevated"
        borderWidth="hairline"
        borderColor="border"
        borderRadius={{ base: 'xl', md: '2xl' }}
        opacity={selected ? 1 : 0.52}
        transform={selected ? 'scale(1)' : 'scale(0.965)'}
        transition="opacity 360ms ease, transform 360ms ease"
        _motionReduce={{ transition: 'none' }}
        _before={{
          content: '""',
          position: 'absolute',
          inset: 0,
          bg: DOMAIN_GRADIENTS[feature.domain],
          opacity: 0.86,
        }}
        _after={{
          content: '""',
          position: 'absolute',
          inset: 0,
          borderRadius: 'inherit',
          border: 'var(--chakra-border-widths-hairline, 0.5px) solid rgba(242, 237, 255, 0.06)',
          pointerEvents: 'none',
        }}
      >
        <Flex position="relative" zIndex={1} direction="column" justify="flex-end" align="flex-start" minH="100%">
          <Text
            fontFamily="mono"
            fontSize="xs"
            letterSpacing="0.12em"
            textTransform="uppercase"
            color="accent"
            mb={6}
          >
            {feature.eyebrow}
          </Text>
          <Heading
            as="h3"
            maxW="13ch"
            fontFamily="heading"
            fontWeight="bold"
            fontSize="clamp(38px, 4.8vw, 76px)"
            lineHeight="0.9"
            letterSpacing="-0.024em"
            color="text"
            textWrap="balance"
          >
            {feature.title}
          </Heading>
          <Text
            maxW="560px"
            mt={6}
            fontFamily="body"
            fontWeight="medium"
            fontSize="clamp(17px, 1.5vw, 22px)"
            lineHeight="1.38"
            color="textMuted"
          >
            {feature.body}
          </Text>
          {feature.ctaLabel && feature.ctaTo ? (
            <RiftboundButton
              as={RouterLink}
              {...{ to: feature.ctaTo } as Record<string, unknown>}
              treatment="primary"
              size="md"
              mt={6}
            >
              {feature.ctaLabel}
            </RiftboundButton>
          ) : null}
        </Flex>

        {feature.videoSrc ? (
          <FeatureCarouselVideo
            src={feature.videoSrc}
            poster={feature.videoPoster}
            alt={feature.imageAlt ?? feature.mockTitle}
          />
        ) : feature.imageSrc ? (
          feature.imageVariant === 'desktop' ? (
            <FeatureCarouselDesktopScreenshot src={feature.imageSrc} alt={feature.imageAlt ?? ''} />
          ) : (
            <FeatureCarouselScreenshot src={feature.imageSrc} alt={feature.imageAlt ?? ''} />
          )
        ) : (
          <FeatureCarouselMockup index={index} title={feature.mockTitle} detail={feature.mockDetail} />
        )}
      </Grid>
    </Flex>
  );
}

function FeatureCarouselVideo({ src, poster, alt }: { src: string; poster?: string; alt: string }) {
  return (
    <Flex
      position="relative"
      zIndex={1}
      justify="center"
      align="center"
      justifySelf="center"
      alignSelf="center"
      w="100%"
      maxW={FEATURE_CAROUSEL_DEVICE_MAX_W}
      aspectRatio={FEATURE_CAROUSEL_DEVICE_ASPECT}
      overflow="hidden"
      borderRadius={{ base: '24px', md: '30px' }}
      borderWidth="hairline"
      borderColor="rgba(242, 237, 255, 0.16)"
      boxShadow="0 22px 70px rgba(0, 0, 0, 0.55)"
      _before={{
        content: '""',
        position: 'absolute',
        inset: '12%',
        borderRadius: 'full',
        bg: 'linear-gradient(135deg, rgba(210, 180, 90, 0.7), rgba(124, 92, 255, 0.5))',
        filter: 'blur(60px)',
        opacity: 0.28,
        pointerEvents: 'none',
      }}
    >
      <video
        src={src}
        poster={poster}
        aria-label={alt}
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', position: 'relative', zIndex: 1 }}
        autoPlay
        loop
        muted
        playsInline
        preload="metadata"
      />
    </Flex>
  );
}

function FeatureCarouselScreenshot({ src, alt }: { src: string; alt: string }) {
  return (
    <Flex
      position="relative"
      zIndex={1}
      justify="center"
      align="center"
      justifySelf="center"
      alignSelf="center"
      w="100%"
      maxW={FEATURE_CAROUSEL_DEVICE_MAX_W}
      aspectRatio={FEATURE_CAROUSEL_DEVICE_ASPECT}
      overflow="hidden"
      borderRadius={{ base: '28px', md: '34px' }}
      p={{ base: 1.5, md: 2 }}
      bg={`linear-gradient(135deg, ${pageBg(0.96)}, ${pageBg(0.78)})`}
      borderWidth="hairline"
      borderColor="rgba(242, 237, 255, 0.1)"
      boxShadow="0 30px 80px rgba(0, 0, 0, 0.42)"
      _before={{
        content: '""',
        position: 'absolute',
        inset: '12%',
        borderRadius: 'full',
        bg: 'linear-gradient(135deg, rgba(210, 180, 90, 0.7), rgba(124, 92, 255, 0.5))',
        filter: 'blur(60px)',
        opacity: 0.28,
        pointerEvents: 'none',
      }}
    >
      <Image
        src={src}
        alt={alt}
        position="relative"
        zIndex={1}
        w="100%"
        h="100%"
        objectFit="cover"
        borderRadius={{ base: '24px', md: '30px' }}
        borderWidth="hairline"
        borderColor="rgba(242, 237, 255, 0.16)"
        boxShadow="0 22px 70px rgba(0, 0, 0, 0.55)"
        decoding="async"
        loading="lazy"
      />
    </Flex>
  );
}

function FeatureCarouselDesktopScreenshot({ src, alt }: { src: string; alt: string }) {
  return (
    <Flex
      position="relative"
      zIndex={1}
      justify="center"
      align="center"
      justifySelf="center"
      alignSelf="center"
      w="100%"
      maxW={{ base: '100%', md: '400px' }}
      direction="column"
      overflow="hidden"
      borderRadius={{ base: '12px', md: '14px' }}
      borderWidth="hairline"
      borderColor="rgba(242, 237, 255, 0.12)"
      boxShadow="0 30px 80px rgba(0, 0, 0, 0.42)"
      bg={pageBg(0.96)}
      _before={{
        content: '""',
        position: 'absolute',
        inset: '8%',
        borderRadius: 'full',
        bg: 'linear-gradient(135deg, rgba(210, 180, 90, 0.7), rgba(124, 92, 255, 0.5))',
        filter: 'blur(60px)',
        opacity: 0.24,
        pointerEvents: 'none',
      }}
    >
      <HStack
        w="100%"
        px={3}
        py={2}
        gap={1.5}
        borderBottomWidth="hairline"
        borderColor="rgba(242, 237, 255, 0.08)"
        bg={pageBg(0.98)}
        position="relative"
        zIndex={1}
      >
        <Box w="2.5" h="2.5" borderRadius="full" bg="rgba(242, 92, 92, 0.75)" />
        <Box w="2.5" h="2.5" borderRadius="full" bg="rgba(210, 180, 90, 0.75)" />
        <Box w="2.5" h="2.5" borderRadius="full" bg="rgba(92, 224, 176, 0.75)" />
      </HStack>
      <Image
        src={src}
        alt={alt}
        position="relative"
        zIndex={1}
        w="100%"
        h="auto"
        objectFit="contain"
        display="block"
        decoding="async"
        loading="lazy"
      />
    </Flex>
  );
}

function FeatureCarouselMockup({ index, title, detail }: { index: number; title: string; detail: string }) {
  return (
    <Flex
      position="relative"
      zIndex={1}
      minH={{ base: '260px', md: '340px' }}
      direction="column"
      justify="space-between"
      overflow="hidden"
      borderRadius="lg"
      p={{ base: 6, md: 10 }}
      bg={`linear-gradient(135deg, ${pageBg(0.94)}, ${pageBg(0.78)})`}
      color="text"
      borderWidth="hairline"
      borderColor="rgba(242, 237, 255, 0.1)"
      boxShadow="0 26px 70px rgba(0, 0, 0, 0.34)"
      _after={{
        content: '""',
        position: 'absolute',
        inset: '16%',
        borderRadius: 'full',
        bg: 'linear-gradient(135deg, rgba(210, 180, 90, 0.9), rgba(124, 92, 255, 0.72))',
        filter: 'blur(54px)',
        opacity: 0.36,
        pointerEvents: 'none',
      }}
    >
      <Text
        position="relative"
        zIndex={1}
        fontFamily="mono"
        fontSize="xs"
        letterSpacing="0.12em"
        color="rgba(242, 237, 255, 0.58)"
      >
        {String(index + 1).padStart(2, '0')}
      </Text>

      <Box position="relative" zIndex={1}>
        <Heading
          as="h4"
          fontFamily="heading"
          fontWeight="bold"
          fontSize="clamp(48px, 5.8vw, 92px)"
          lineHeight="0.84"
          letterSpacing="-0.035em"
          textTransform="uppercase"
          maxW="7ch"
          mb={5}
        >
          {title}
        </Heading>
        <Text fontSize="sm" color="textMuted" lineHeight="tall" maxW="320px">
          {detail}
        </Text>
      </Box>
    </Flex>
  );
}
